import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AgencyProviderPriceOverrideModel, AgencyProviderPriceOverrideRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['agencyProviderPriceOverrides']

type AgencyProviderPriceOverrideFilters = {
  agency_id?: number,
  string?: string,
  provider_id?: number,
}

type UseAgencyProviderPriceOverridesOptions = {
  filters?: AgencyProviderPriceOverrideFilters,
  performHttpRequests?: boolean,
  requestOptions?: AgencyProviderPriceOverrideRequestOptions,
}

function useAgencyProviderPriceOverrides(options: UseAgencyProviderPriceOverridesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    string: filterString,
    provider_id: filterProviderId,
  } = filters

  const {
    updatedEntities: { agencyProviderPriceOverrides: agencyProviderPriceOverridesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { agencyProviderPriceOverrides } = useSelector(reduxState => reduxState.entities)

  const filteredAgencyProviderPriceOverrides = useMemo(() => {
    const filtered = Object.values(agencyProviderPriceOverrides)
      .filter((agencyProviderPriceOverride: AgencyProviderPriceOverrideModel) => {
        const {
          // @ts-expect-error TS2339
          agency_id, id, provider_id, title,
        } = agencyProviderPriceOverride

        const matchId = id.toString() === filterString
        const matchTitle = matchFilterString(title, filterString)
        const matchString = matchId || matchTitle

        const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)
        const matchProviderId = matchFilterNumber(provider_id, filterProviderId)

        return matchAgencyId && matchProviderId && matchString
      })

    return sortArrayBy(filtered, 'asc')
  }, [agencyProviderPriceOverridesUpdatedAt, JSON.stringify(filters)])

  const filteredAgencyProviderPriceOverridesCount = filteredAgencyProviderPriceOverrides.length
  const hasFilteredAgencyProviderPriceOverrides = !!filteredAgencyProviderPriceOverridesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAgencyProviderPriceOverridesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAgencyProviderPriceOverrides } = useReduxAction(
    'agencyProviderPriceOverrides',
    'loadAgencyProviderPriceOverrides',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyProviderPriceOverrides,
    filteredAgencyProviderPriceOverridesCount,
    hasFilteredAgencyProviderPriceOverrides,
    loading: loadingAgencyProviderPriceOverrides,
  }
}

export default useAgencyProviderPriceOverrides
